import { CheckCircle2 } from 'app/design/icons/check-circle2';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Linking, Pressable } from 'react-native';
import { useAmbassadorLearnMoreUrl } from './constants';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { AMBASSADOR_ADVANTAGES_KEYS, AMBASSADOR_MISSION_KEYS } from './ambassador-shared.constants';

export { AMBASSADOR_ADVANTAGES_KEYS, AMBASSADOR_MISSION_KEYS } from './ambassador-shared.constants';

const theme = createDSTheme();

export function AmbassadorAdvantagesList({
  listGap = 'spacing.general.x2',
  rowGap = 'spacing.general.x2',
}: {
  listGap?: 'spacing.general.x1' | 'spacing.general.x2';
  rowGap?: 'spacing.general.x2' | 'spacing.general.x4';
}) {
  const { t } = useTranslation('ambassador');
  const [expandedAdvantage, setExpandedAdvantage] = useState<(typeof AMBASSADOR_ADVANTAGES_KEYS)[number] | null>(null);

  return (
    <Column
      width="100%"
      backgroundColor="surface.secondary"
      borderRadius="radius.xl"
      padding="spacing.general.x4"
      gap={listGap}
    >
      {AMBASSADOR_ADVANTAGES_KEYS.map((key, idx) => {
        const isExpanded = expandedAdvantage === key;
        const body = t(`advantages.${key}.body`, { defaultValue: '' });
        const hasBody = body.length > 0;

        return (
          <Column
            key={key}
            paddingVertical="spacing.general.x2"
            style={
              idx < AMBASSADOR_ADVANTAGES_KEYS.length - 1
                ? { borderBottomWidth: 1, borderBottomColor: 'rgba(0,90,93,0.15)' }
                : undefined
            }
          >
            <Pressable
              onPress={() => hasBody && setExpandedAdvantage(isExpanded ? null : key)}
              style={{ width: '100%' }}
            >
              <Row alignItems="center" width="100%">
                <Row alignItems="center" gap={rowGap} flex={1} style={{ minWidth: 0, flexShrink: 1 }}>
                  <CheckCircle2 color={theme.colors['text.brand']} fillColor={theme.colors['surface.default']} />
                  <Column flex={1} style={{ minWidth: 0, flexShrink: 1 }}>
                    <Text fontWeight="700" variant="bodyBase" style={{ flexShrink: 1 }}>
                      {t(`advantages.${key}.title`)}
                    </Text>
                  </Column>
                </Row>
                {hasBody ? (
                  <Column
                    alignItems="center"
                    justifyContent="center"
                    marginLeft={rowGap}
                    style={{ width: 18, flexShrink: 0 }}
                  >
                    {isExpanded ? (
                      <IconChevronUp strokeWidth={ICON_STROKE_WIDTH} size={18} color={theme.colors['text.brand']} />
                    ) : (
                      <IconChevronDown strokeWidth={ICON_STROKE_WIDTH} size={18} color={theme.colors['text.brand']} />
                    )}
                  </Column>
                ) : null}
              </Row>
            </Pressable>
            {isExpanded && hasBody ? (
              <Text variant="bodyBase" style={{ marginTop: theme.spacing['spacing.general.x2'], marginLeft: 40 }}>
                {body}
              </Text>
            ) : null}
          </Column>
        );
      })}
    </Column>
  );
}

export function AmbassadorMissionSteps({ textVariant = 'bodyLarge' }: { textVariant?: 'bodyBase' | 'bodyLarge' }) {
  const { t } = useTranslation('ambassador');

  return (
    <Column gap="spacing.general.x2" width="100%">
      {AMBASSADOR_MISSION_KEYS.map((key, idx) => (
        <Row key={key} alignItems="flex-start" gap="spacing.general.x4">
          <Column
            style={{
              width: 32,
              height: 32,
              borderRadius: 16,
              backgroundColor: theme.colors['text.primary'],
              justifyContent: 'center',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <Text color="surface.default" variant={`${textVariant}Strong`}>
              {idx + 1}
            </Text>
          </Column>
          <Text flex={1} variant={textVariant} style={{ paddingTop: 6 }}>
            {t(`mission.${key}`)}
          </Text>
        </Row>
      ))}
    </Column>
  );
}

export function AmbassadorLearnMoreButton({
  alignSelf = 'flex-start',
  paddingLeft = 0,
}: {
  alignSelf?: 'flex-start';
  paddingLeft?: number;
}) {
  const { t } = useTranslation('ambassador');

  const ambassadorLearnMoreUrl = useAmbassadorLearnMoreUrl();

  return (
    <Button
      onPress={() => void Linking.openURL(ambassadorLearnMoreUrl).catch((error: unknown) => console.error(error))}
      variant="plain"
      style={{ alignSelf, paddingLeft }}
    >
      <Row alignItems="center" gap="spacing.general.x1">
        <Text color="text.brand" variant="bodyBase">
          {t('mission.learnMore')}
        </Text>
        <Text color="text.brand" variant="bodyBase">
          ↗
        </Text>
      </Row>
    </Button>
  );
}
